import React from 'react';
import { connect } from 'react-redux';

import { IRecordById, IFieldById } from '../../../../../types/core';
import { IRefTableColumn, ITableConfig } from '../../../../../types/space';
import Content from '../Content';
import RefRenderer from '../Renderer';
import { checkIfLookupIsLookingAtRecordReference } from '../../../../../modules/core/helpers';
import { ColumnType } from '../../../../../modules/core/graphql/types';
import { IRootState } from '../../../../../modules/stores';

interface IProps {
  record: IRecordById;
  fields: IFieldById[];
  // *ONLY* columns that are record refs should have another tableColumn nested inside
  tableColumns: IRefTableColumn[];
  showRefAsText: boolean;
  visibleNodes: { [key: string]: string };
  depth?: number;
  spaceId: string;
}

/**
 * Literally took me half a millenia to figure this out. In order to make the references tables underneath the record, while
 * utilizing the full power of tables, we can make it as a td that spans the fill column # of the parent columns.
 * We are recursively accessing the components depending how large the depth is.
 */
const Row: React.FC<IProps> = ({
  fields,
  record,
  tableColumns,
  showRefAsText,
  visibleNodes,
  depth,
  spaceId,
}) => {
  // force show reference as text if at depth limit
  const refAsText = depth && depth > 2 ? true : showRefAsText;
  // Get the number of columns that are not record references
  const numColsNotRef = fields.reduce((count, field) => {
    return field && field.type !== (ColumnType.RECORD_REFERENCE || ColumnType.SUBTABLE)
      ? count + 1
      : count;
  }, 0);

  return (
    <>
      <tr>
        {fields.map((refField, index) => {
          // Protection, most likely happening because of permissions
          if (!refField) return null;

          const isNodeVisible =
            (refField.type === ColumnType.RECORD_REFERENCE ||
              refField.type === ColumnType.SUBTABLE) &&
            visibleNodes[refField.typeOptions.foreignTableId];

          if (
            ((refField.type === ColumnType.RECORD_REFERENCE ||
              refField.type === ColumnType.SUBTABLE) &&
              !refAsText &&
              isNodeVisible) ||
            (checkIfLookupIsLookingAtRecordReference(refField) && !refAsText)
          )
            return null;

          /** All of these components are being used by the elements themselves. It's pretty
          tricky that we are sharing the * same components. But we can only do so by passing
          record id. */
          return (
            <td style={{ padding: '5px' }} key={`fieldIdItem-${index}`}>
              <div>
                <Content
                  field={refField}
                  record={record}
                  showRefAsText={refAsText}
                  spaceId={spaceId}
                />
              </div>
            </td>
          );
        })}
      </tr>

      {fields.map((refField, index) => {
        // Protection, most likely happening because of permissions
        if (!refField) return null;

        const isNodeVisible =
          (refField.type === ColumnType.RECORD_REFERENCE ||
            refField.type === ColumnType.SUBTABLE) &&
          visibleNodes[refField.typeOptions.foreignTableId];

        if (
          !(
            ((refField.type === ColumnType.RECORD_REFERENCE ||
              refField.type === ColumnType.SUBTABLE) &&
              !refAsText &&
              isNodeVisible) ||
            (checkIfLookupIsLookingAtRecordReference(refField) && !refAsText)
          )
        )
          return null;
        /**
         * This is where shit gets real.
         * Because this is a record ref, there is another tableColumn object inside the parent tableColumn.
         * We can pass in the next level table Column and then recursively render itself.
         */
        const currentTableColumnConfig = tableColumns.find((col) => col.id === refField.id);
        if (!currentTableColumnConfig) return null;

        return (
          <tr key={`fieldIdRec-${index}`}>
            <td colSpan={numColsNotRef} style={{ padding: '10px' }}>
              <RefRenderer
                key={`fieldIdRecRend-${index}`}
                config={currentTableColumnConfig as ITableConfig}
                fieldId={refField.id}
                recordId={record.id}
                isLookup={checkIfLookupIsLookingAtRecordReference(refField)}
                depth={depth}
                spaceId={spaceId}
              />
            </td>
          </tr>
        );
      })}
    </>
  );
};

const mapState = ({ core }: IRootState) => ({
  visibleNodes: core.visibleNodes,
});

export default connect(mapState)(Row);
